import React, { useState, useEffect } from 'react';
import { invoke } from '@tauri-apps/api/tauri';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  Activity, 
  Zap, 
  AlertCircle, 
  CheckCircle, 
  Users, 
  Brain, 
  Shield, 
  Workflow,
  Database,
  MessageSquare
} from 'lucide-react';

interface AgentEndpoint {
  name: string;
  category: string;
  description: string;
  capabilities: string[];
  status: string;
  health_endpoint: string;
  api_endpoint: string;
}

interface AgentRegistry {
  agents: AgentEndpoint[];
  api_gateway_url: string;
  total_count: number;
  healthy_count: number;
}

interface AgentResponse {
  agent_name: string;
  task_id: string;
  status: string;
  result: any;
  execution_time_ms: number;
}

interface AgentEcosystemProps {
  selectedPersona?: string;
  onAgentInvoke?: (agentName: string, result: AgentResponse) => void;
}

export const AgentEcosystem: React.FC<AgentEcosystemProps> = ({ 
  selectedPersona, 
  onAgentInvoke 
}) => {
  const [registry, setRegistry] = useState<AgentRegistry | null>(null);
  const [personaAgents, setPersonaAgents] = useState<AgentEndpoint[]>([]);
  const [healthStatus, setHealthStatus] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [invoking, setInvoking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAgentRegistry();
    if (selectedPersona) {
      loadPersonaAgents(selectedPersona);
    }
  }, [selectedPersona]);

  useEffect(() => {
    const interval = setInterval(() => {
      checkAgentHealth();
    }, 30000); // Check health every 30 seconds

    return () => clearInterval(interval);
  }, [registry]);

  const loadAgentRegistry = async () => {
    try {
      setLoading(true);
      const agentRegistry = await invoke<AgentRegistry>('get_agent_registry');
      setRegistry(agentRegistry);
      await checkAgentHealth();
    } catch (err) {
      setError(`Failed to load agent registry: ${err}`);
    } finally {
      setLoading(false);
    }
  };

  const loadPersonaAgents = async (personaId: string) => {
    try {
      const agents = await invoke<AgentEndpoint[]>('get_agents_for_persona', { personaId });
      setPersonaAgents(agents);
    } catch (err) {
      console.error('Failed to load persona agents:', err);
    }
  };

  const checkAgentHealth = async () => {
    try {
      const health = await invoke<Record<string, boolean>>('check_agents_health');
      setHealthStatus(health);
    } catch (err) {
      console.error('Failed to check agent health:', err);
    }
  };

  const invokeAgent = async (agentName: string, taskDescription: string) => {
    try {
      setInvoking(agentName);
      const response = await invoke<AgentResponse>('invoke_agent_task', {
        agentName,
        taskDescription,
        parameters: {}
      });
      
      onAgentInvoke?.(agentName, response);
    } catch (err) {
      console.error('Failed to invoke agent:', err);
    } finally {
      setInvoking(null);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'legal': return <Shield className="w-5 h-5" />;
      case 'financial': return <Database className="w-5 h-5" />;
      case 'ai': return <Brain className="w-5 h-5" />;
      case 'integration': return <Workflow className="w-5 h-5" />;
      case 'communication': return <MessageSquare className="w-5 h-5" />;
      default: return <Activity className="w-5 h-5" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category.toLowerCase()) {
      case 'legal': return 'bg-blue-100 text-blue-800';
      case 'financial': return 'bg-green-100 text-green-800';
      case 'ai': return 'bg-purple-100 text-purple-800';
      case 'integration': return 'bg-orange-100 text-orange-800';
      case 'communication': return 'bg-pink-100 text-pink-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 animate-spin" />
            Loading Agent Ecosystem
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Progress value={50} className="w-full" />
          <p className="text-sm text-muted-foreground mt-2">
            Connecting to ChittyOS Agent Registry...
          </p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <AlertCircle className="w-5 h-5" />
            Agent Ecosystem Error
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-600">{error}</p>
          <Button 
            onClick={loadAgentRegistry} 
            className="mt-4"
            variant="outline"
          >
            Retry Connection
          </Button>
        </CardContent>
      </Card>
    );
  }

  const displayAgents = selectedPersona ? personaAgents : registry?.agents || [];
  const healthyCount = displayAgents.filter(agent => healthStatus[agent.name]).length;

  return (
    <div className="space-y-6">
      {/* Registry Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            ChittyOS Agent Ecosystem
          </CardTitle>
          <CardDescription>
            Connected to {registry?.api_gateway_url} • {registry?.total_count} total agents
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{healthyCount}</div>
              <div className="text-sm text-muted-foreground">Healthy</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {displayAgents.length - healthyCount}
              </div>
              <div className="text-sm text-muted-foreground">Offline</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {displayAgents.length}
              </div>
              <div className="text-sm text-muted-foreground">
                {selectedPersona ? 'Recommended' : 'Available'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {new Set(displayAgents.map(a => a.category)).size}
              </div>
              <div className="text-sm text-muted-foreground">Categories</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Agent List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {displayAgents.map((agent) => (
          <Card key={agent.name} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  {getCategoryIcon(agent.category)}
                  <CardTitle className="text-lg">{agent.name}</CardTitle>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={getCategoryColor(agent.category)}>
                    {agent.category}
                  </Badge>
                  {healthStatus[agent.name] ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-red-600" />
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <CardDescription className="line-clamp-2">
                {agent.description}
              </CardDescription>
              
              {agent.capabilities.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Capabilities:</p>
                  <div className="flex flex-wrap gap-1">
                    {agent.capabilities.slice(0, 3).map((capability) => (
                      <Badge key={capability} variant="secondary" className="text-xs">
                        {capability}
                      </Badge>
                    ))}
                    {agent.capabilities.length > 3 && (
                      <Badge variant="secondary" className="text-xs">
                        +{agent.capabilities.length - 3} more
                      </Badge>
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  size="sm"
                  onClick={() => invokeAgent(agent.name, 'System health check')}
                  disabled={!healthStatus[agent.name] || invoking === agent.name}
                  className="flex-1"
                >
                  {invoking === agent.name ? (
                    <>
                      <Activity className="w-4 h-4 animate-spin mr-2" />
                      Invoking...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4 mr-2" />
                      Invoke
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {displayAgents.length === 0 && (
        <Card>
          <CardContent className="text-center py-8">
            <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-medium mb-2">No Agents Available</p>
            <p className="text-muted-foreground mb-4">
              {selectedPersona 
                ? `No agents configured for the ${selectedPersona} persona.`
                : 'Unable to connect to the agent registry.'
              }
            </p>
            <Button onClick={loadAgentRegistry} variant="outline">
              Refresh Registry
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};